import * as R from 'ramda';
import * as date from 'date-fns';

import getFilters from '../../selectors/getSelectedFilters';
import getUseRightsholderAttribution from '../../utils/getUseRightsholderAttribution';
import { isFailedToFetchError } from '../../utils/requestErrorHandlers';
import fetchMediaValueReportCached, {
    isMediaValueReportRequestError,
} from './utils/fetchMediaValueReportCached';
import hasFiltersChanged from './utils/hasFiltersChanged';

const isValidRhPlacementId = (
    id,
    selectedRightsholderIds = [],
    placementsList = {},
) => {
    if (!id || typeof id !== 'string' || !id.includes('-')) return false;

    const [rhId] = id.split('-');
    const shouldFilterByRightsholder = selectedRightsholderIds.length > 0;
    if (shouldFilterByRightsholder && !selectedRightsholderIds.includes(rhId)) {
        return false;
    }

    return Object.prototype.hasOwnProperty.call(placementsList, id);
};

const getTopPlacementGraph =
    store =>
    async (state, granularity = 'week') => {
        const userClientId = state.user.selectedUserClientId;
        const clientId = state.user.selectedClientId;
        const filters = state.clients[userClientId].filters;
        const client = state.user.clientsById[clientId];
        const useRightsholderAttribution = getUseRightsholderAttribution(
            client,
            clientId,
        );

        const changesId = filters.changesId;
        const { total } = state.clients[userClientId].home.topPlacement;

        // The graph is built from the top-10 list, so it must wait for the
        // list requested under the current filters. While that request is in
        // flight its caller fetches the graph again once it lands; if it
        // failed, the graph cannot be built either.
        if (total.changesId !== changesId) {
            if (!total.isLoading && total.hasError) {
                store.setState(
                    R.assocPath(
                        [
                            'clients',
                            userClientId,
                            'home',
                            'topPlacement',
                            'graph',
                        ],
                        {
                            ...state.clients[userClientId].home.topPlacement
                                .graph,
                            isLoading: false,
                            hasError: true,
                        },
                        state,
                    ),
                    true,
                );
            }
            return;
        }

        // Sets Cards as loading
        const loadingState = R.pipe(
            R.assocPath(
                [
                    'clients',
                    userClientId,
                    'home',
                    'topPlacement',
                    'graph',
                    'isLoading',
                ],
                true,
            ),
            R.assocPath(
                [
                    'clients',
                    userClientId,
                    'home',
                    'topPlacement',
                    'graph',
                    'hasError',
                ],
                false,
            ),
        )(state);
        store.setState(loadingState);

        const globFilters = getFilters(filters);

        const list =
            store.getState().clients[userClientId].home.topPlacement.total
                .list || [];
        const selectedRightsholderIds = Array.isArray(
            globFilters.rightsholderIds,
        )
            ? globFilters.rightsholderIds.map(String)
            : [];
        const placementsList = filters?.placements?.list || {};

        // Get data for graphs based on the top 10 list and keep only ids
        // valid for the current attribution mode and selected rightsholders.
        const rightsholderPlacementIds = list.reduce((arr, { id }) => {
            if (
                isValidRhPlacementId(
                    id,
                    selectedRightsholderIds,
                    placementsList,
                )
            ) {
                arr.push(id);
            }
            return arr;
        }, []);

        let graphData = { values: [] };
        if (rightsholderPlacementIds.length > 0) {
            const graphQuery = {
                ...globFilters,
                groupBy: ['rightsholder', 'rightsholderPlacement', 'date'],
                orderBy: ['date-asc'],
                displayColumns: ['sponsorMediaValue'],
                rightsholderPlacementIds,
                includeTextTypeExposures: true,
                ...(useRightsholderAttribution
                    ? { useRightsholderAttribution: true }
                    : {}),
            };

            try {
                graphData = await fetchMediaValueReportCached({
                    ...graphQuery,
                    granularity,
                });
            } catch (error) {
                if (
                    !isFailedToFetchError(error) &&
                    !isMediaValueReportRequestError(error)
                ) {
                    throw error;
                }

                if (hasFiltersChanged(store, userClientId, changesId)) return;

                store.setState(
                    R.pipe(
                        R.assocPath(
                            [
                                'clients',
                                userClientId,
                                'home',
                                'topPlacement',
                                'graph',
                                'isLoading',
                            ],
                            false,
                        ),
                        R.assocPath(
                            [
                                'clients',
                                userClientId,
                                'home',
                                'topPlacement',
                                'graph',
                                'hasError',
                            ],
                            true,
                        ),
                    )(store.getState()),
                    true,
                );
                return;
            }
        }

        const graphValues = Array.isArray(graphData?.values)
            ? graphData.values
            : [];

        const startDate =
            globFilters.startDate || R.path([0, 'date'], graphValues);
        const endDate =
            globFilters.endDate ||
            (graphValues.length > 0
                ? R.path([graphValues.length - 1, 'date'], graphValues)
                : undefined);

        const parsedStartDate = startDate
            ? date.parseISO(startDate)
            : date.parseISO(
                  date.format(date.subMonths(new Date(), 3), 'yyyy-MM-dd'),
              );
        const parsedEndDate = endDate
            ? date.parseISO(endDate)
            : date.parseISO(date.format(new Date(), 'yyyy-MM-dd'));

        // generate Labels based on granularity
        const datesRange = date.eachDayOfInterval({
            start: parsedStartDate,
            end: parsedEndDate,
        });

        const labels = datesRange.reduce((acc, d) => {
            const currentDate = date.format(d, 'yyyy-MM-dd');

            if (granularity === 'day') acc.push(currentDate);
            if (
                granularity === 'week' &&
                date.isMonday(date.parseISO(currentDate))
            )
                acc.push(currentDate);
            if (
                granularity === 'month' &&
                date.isFirstDayOfMonth(date.parseISO(currentDate))
            )
                acc.push(currentDate);
            return acc;
        }, []);

        const dataMap = graphValues.reduce((acc, o) => {
            const itExists = !!acc[o.date];
            const rhId = o.rightsholderId;
            const plcId = o.rightsholderPlacementId[rhId];
            const id = `${rhId}-${plcId}`;
            if (itExists) acc[o.date][id] = o.sponsorMediaValue;
            else acc[o.date] = { [id]: o.sponsorMediaValue };
            return acc;
        }, {});

        const data = list.reduce((acc, plc) => {
            const values = labels.map(date => {
                return R.path([date, plc.id], dataMap) || 0;
            });

            acc[plc.id] = {
                id: plc.id,
                label: plc.name,
                data: values,
            };

            return acc;
        }, {});

        if (hasFiltersChanged(store, userClientId, changesId)) return;

        store.setState(
            R.assocPath(
                ['clients', userClientId, 'home', 'topPlacement', 'graph'],
                {
                    isLoading: false,
                    hasError: false,
                    labels,
                    data,
                    granularity,
                    changesId,
                },
                store.getState(),
            ),
            true,
        );
    };

export default getTopPlacementGraph;
